import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { CheckoutPageId } from 'checkout/constants/pages'
import { isTourV2Item } from 'lib/checkout/checkoutUtils'
import { FLIGHTS_DISABLED_GEO_STATES } from 'constants/config/region'
import { getCurrentCheckoutPage } from 'checkout/lib/navigation/getCurrentCheckoutPage'
import { getNonCharterFlightItems } from 'checkout/selectors/view/flights'
import getObjectKey from 'lib/object/getObjectKey'
import { getTourV2Items } from 'checkout/selectors/view/toursv2'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import { buildTourFlightSearch } from 'checkout/lib/tourFlightSearch'
import { createSelector } from 'reselect'
import getTourFlightAirportsKey from 'checkout/lib/getTourFlightAirportsKey'

export const getSelectedTourFlightAirportsRequest = createSelector(
  getTourV2Items,
  (state: App.State) => state.tour.tourFlightAirports,
  (items, requests) => {
    const item = items[0]
    const departureId = item?.purchasableOption.fkDepartureId
    return item && departureId ? requests[getTourFlightAirportsKey(item.offerId, departureId)] : undefined
  },
)

export const getTourFlightSearch = createSelector(
  getTourV2Items,
  getDefaultAirportCode,
  (state: App.State) => getSelectedTourFlightAirportsRequest(state)?.data,
  (state: App.State) => state.checkout.cart.tourFlightSearch,
  (items, homeAirport, airports, edits) => {
    const searches = buildTourFlightSearch(items, homeAirport, airports, edits)
    return {
      departing: { ...searches.departing, key: getObjectKey(searches.departing) },
      returning: { ...searches.returning, key: getObjectKey(searches.returning) },
    }
  },
)

export function isTourFlightUpsellAvailable(state: App.State): boolean {
  return !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.flightsTourUpsellEnabled] &&
    state.geo.currentRegionHasFlights &&
    getSelectedTourFlightAirportsRequest(state)?.data?.flightsIncluded !== true &&
    !FLIGHTS_DISABLED_GEO_STATES.includes(state.geo.geoStateName ?? '') &&
    state.checkout.cart.items.some(item => isTourV2Item(item) && !!item.purchasableOption.fkDepartureId)
}

/** Home airports can be supplied in the modal; tour endpoints and dates cannot. */
export function isTourFlightStepAvailable(state: App.State): boolean {
  const tour = getTourV2Items(state)[0]
  const request = getSelectedTourFlightAirportsRequest(state)
  return isTourFlightUpsellAvailable(state) && !!tour?.startDate && !!tour.endDate &&
    !request?.error && !!request?.data?.arrivalAirportCode && !!request.data.departureAirportCode
}

export function getTourFlightLegForPage(page: string | undefined) {
  if (page === CheckoutPageId.DepartingFlights) return 'departing'
  if (page === CheckoutPageId.ReturningFlights) return 'returning'
  return undefined
}

export function getTourFlightSearchForPage(state: App.State) {
  const leg = isTourFlightUpsellAvailable(state) ? getTourFlightLegForPage(getCurrentCheckoutPage(state)) : undefined
  return leg ? getTourFlightSearch(state)[leg] : undefined
}

export function isTourFlightSearchIncompleteForPage(state: App.State): boolean {
  const search = getTourFlightSearchForPage(state)
  if (!search) return false
  const itinerary = search.itinerary[0]
  return !itinerary.departureDate || !itinerary.departureAirport || !itinerary.arrivalAirport
}

export function getTourFlightItemForPage(state: App.State): App.Checkout.FlightItem | undefined {
  const leg = getTourFlightLegForPage(getCurrentCheckoutPage(state))
  return leg ? getNonCharterFlightItems(state).find(item => item.tourFlightLeg === leg) : undefined
}

export function getSelectedTourFlightItems(state: App.State): Array<App.Checkout.FlightItem> {
  const items = getNonCharterFlightItems(state).filter(item => item.tourFlightLeg && item.flights.length > 0)
  return ['departing', 'returning'].flatMap(leg => items.filter(item => item.tourFlightLeg === leg))
}

export function getTourFlightExpiry(state: App.State): number | undefined {
  const selected = getSelectedTourFlightItems(state)
  if (selected.some(item => tourFlightHasError(item, state))) return 0
  const expiries = selected.map(item => item.expiry).filter((expiry): expiry is number => expiry !== undefined)
  return expiries.length ? Math.min(...expiries) : undefined
}

export function tourFlightNeedsRefresh(item: App.Checkout.FlightItem, state: App.State): boolean {
  return item.flights.length > 0 && (
    tourFlightHasError(item, state) || (item.expiry !== undefined && item.expiry <= Date.now())
  )
}

export function tourFlightHasError(item: App.Checkout.FlightItem, state: App.State): boolean {
  return !!item.fareFetchError || item.flights.some(flight => !!state.flights.journeyErrors[getObjectKey({
    journeyId: flight.journeyId,
    searchId: item.searchId,
    fareFamilyId: flight.fareFamily?.id,
  })])
}

/** Keep standard flight routes available; gate tour entry, including saved URLs. */
export function isTourFlightSelectionStepAvailable(state: App.State): boolean {
  return !state.checkout.cart.items.some(isTourV2Item) || isTourFlightStepAvailable(state)
}
